/**
 * Demand Scaler Utility
 * Scales demand data based on scenario configuration
 * Supports both real hourly data and synthetic monthly distributions
 */

/**
 * Get month index (0-11) from hour index (0-8783)
 * @param {number} hour - Hour index (0-8783)
 * @returns {number} Month index (0-11)
 */
const getMonthFromHour = (hour) => {
  // 2024 is a leap year with 366 days = 8784 hours
  // Approximate days in each month for 2024
  const daysPerMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let cumulativeHours = 0;
  for (let month = 0; month < 12; month++) {
    const monthHours = daysPerMonth[month] * 24;
    if (hour < cumulativeHours + monthHours) {
      return month;
    }
    cumulativeHours += monthHours;
  }

  return 11; // December for any overflow
};

/**
 * Calculate hourly demand from monthly distribution
 * CRITICAL: Monthly distribution is NET customer demand (after all losses)
 * This function returns NET demand - SimulationEngine's calculateDemand
 * handles the conversion to An-værk/Ab-værk requirements
 * @param {number} hour - Hour index (0-8783)
 * @param {Array<number>} monthlyDistribution - Monthly NET GWh [Jan...Dec]
 * @returns {number} NET demand in MW (customer level)
 */
const calculateHourlyDemandFromMonthly = (hour, monthlyDistribution) => {
  const month = getMonthFromHour(hour);
  const monthlyNetGWh = monthlyDistribution[month]; // NET customer demand

  // Get days in this month (2024 is leap year)
  const daysPerMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const daysInMonth = daysPerMonth[month];
  const hoursInMonth = daysInMonth * 24;

  // Convert monthly NET GWh to MWh
  const monthlyNetMWh = monthlyNetGWh * 1000;

  // Calculate average hourly NET demand for this month
  // NOTE: We return NET demand here - calculateDemand() in constants.js
  // handles the conversion to An-værk/Ab-værk requirements
  const avgHourlyNetMW = monthlyNetMWh / hoursInMonth;

  // Flat distribution - no variation factors
  return avgHourlyNetMW;
};

/**
 * Create a scaled data provider from scenario config
 * @param {Object} baseDataProvider - Original data provider with real 2024 data
 * @param {Object} scenarioConfig - Scenario configuration
 * @returns {Object} Scaled data provider
 */
export const createScaledDataProvider = (baseDataProvider, scenarioConfig) => {
  if (!baseDataProvider || !scenarioConfig) {
    console.error('Missing baseDataProvider or scenarioConfig');
    return baseDataProvider;
  }

  const {
    monthlyDistribution,
    annualDemandGWh,
    energySources,
  } = scenarioConfig;

  return {
    // Spot prices are always from real 2024 DK2 data
    getSpotPrice: (hour) => {
      return baseDataProvider.getSpotPrice(hour);
    },

    // Solar data - only if enabled in energy sources
    getSolarMW: (hour) => {
      if (!energySources.solar) {
        return 0; // Solar disabled in this scenario
      }
      return baseDataProvider.getSolarMW(hour);
    },

    // Waste heat - only if enabled in energy sources
    getWasteHeatMW: (hour) => {
      if (!energySources.wasteHeat) {
        return 0; // Waste heat disabled in this scenario
      }
      return baseDataProvider.getWasteHeatMW(hour);
    },

    // Demand - scaled based on scenario
    getDemandMW: (hour) => {
      // Use monthly distribution if provided, otherwise scale from real data
      if (monthlyDistribution && monthlyDistribution.length === 12) {
        // Synthetic demand from monthly distribution
        return calculateHourlyDemandFromMonthly(hour, monthlyDistribution);
      } else {
        // Scale real data proportionally
        const originalDemand = baseDataProvider.getDemandMW(hour);
        const scalingFactor = annualDemandGWh / 45.589; // Skælskør baseline
        return originalDemand * scalingFactor;
      }
    },
  };
};

/**
 * Validate that total demand matches target annual GWh
 * @param {Object} dataProvider - Data provider to validate
 * @param {number} targetGWh - Target annual demand in GWh
 * @returns {Object} Validation result
 */
export const validateAnnualDemand = (dataProvider, targetGWh) => {
  let totalMWh = 0;

  for (let hour = 0; hour < 8783; hour++) {
    const demandMW = dataProvider.getDemandMW(hour);
    totalMWh += demandMW;
  }

  const actualGWh = totalMWh / 1000;
  const difference = actualGWh - targetGWh;
  const percentDiff = (difference / targetGWh) * 100;

  return {
    targetGWh,
    actualGWh,
    difference,
    percentDiff,
    isValid: Math.abs(percentDiff) < 1.0, // Allow 1% tolerance
  };
};

/**
 * Calculate monthly breakdown from hourly data
 * Useful for verifying monthly distributions
 * @param {Object} dataProvider - Data provider
 * @returns {Array<number>} Monthly GWh values [Jan...Dec]
 */
export const calculateMonthlyBreakdown = (dataProvider) => {
  const daysPerMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const monthlyMWh = new Array(12).fill(0);

  let hour = 0;
  for (let month = 0; month < 12; month++) {
    const hoursInMonth = daysPerMonth[month] * 24;

    for (let h = 0; h < hoursInMonth; h++) {
      if (hour < 8783) {
        monthlyMWh[month] += dataProvider.getDemandMW(hour);
        hour++;
      }
    }
  }

  return monthlyMWh.map(mwh => mwh / 1000); // Convert to GWh
};

export default {
  createScaledDataProvider,
  validateAnnualDemand,
  calculateMonthlyBreakdown,
  getMonthFromHour,
  calculateHourlyDemandFromMonthly,
};
